import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Toast from '../../../../components/Common/Toast';
import { REPORT_PAGES } from './shared';

export default function PriceTrackingShell({
  title,
  subtitle,
  filters,
  updateFilter,
  suppliers,
  products,
  onQuery,
  loading,
  children,
  toast,
  onToastClose,
  extraActions,
  hideInsumosOnlyFilter = false,
}) {
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1500px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/purchase-orders" className="hover:text-teal-600">Compras</Link>
            <span className="mx-2">/</span>
            <Link to="/compras/reports/document-register" className="hover:text-teal-600">Reportes</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">{title}</span>
          </nav>

          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{title}</h1>
              {subtitle ? <p className="text-gray-600 mt-1 max-w-2xl">{subtitle}</p> : null}
            </div>
            {extraActions}
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proveedor</label>
                <select
                  value={filters.supplierId}
                  onChange={(e) => updateFilter('supplierId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos</option>
                  {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Insumo / producto</label>
                <select
                  value={filters.productId}
                  onChange={(e) => updateFilter('productId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.sku ? `${p.sku} — ` : ''}{p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
                <select
                  value={filters.currency}
                  onChange={(e) => updateFilter('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todas</option>
                  <option value="PEN">PEN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Alerta desde (%)</label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={filters.spikeThreshold}
                  onChange={(e) => updateFilter('spikeThreshold', parseInt(e.target.value, 10) || 10)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {!hideInsumosOnlyFilter ? (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.insumosOnly}
                    onChange={(e) => updateFilter('insumosOnly', e.target.checked)}
                    className="rounded border-gray-300 text-teal-600"
                  />
                  Solo insumos (catálogo)
                </label>
              ) : null}
              <input
                type="text"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                placeholder="Buscar producto o proveedor..."
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              <button
                type="button"
                onClick={onQuery}
                disabled={loading}
                className="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md font-medium text-sm"
              >
                {loading ? 'Cargando...' : 'Consultar'}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {REPORT_PAGES.map((page) => (
              <Link
                key={page.id}
                to={page.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === page.path || (page.id === 'products' && location.pathname.startsWith(`${page.path}/`))
                    ? 'bg-teal-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {page.icon} {page.label}
              </Link>
            ))}
          </div>

          {children}
        </div>
      </div>

      <Toast
        isOpen={toast?.isOpen}
        message={toast?.message}
        type={toast?.type}
        onClose={onToastClose}
      />
    </div>
  );
}
